/**
 * IMPORTS
 */
import {LoginTokenError}
    from 'src/aggregates/user/err/logintokenerror';
import {PreAuthenticationError}
    from 'src/aggregates/user/err/preauthenticationerror';
import {PreAuthorizationError}
    from 'src/aggregates/user/err/preauthorizationerror';
import {CREATE_NPS_FEEDBACK} from 'src/aggregates/user/mutation';
import {CREATE_QUEUE} from 'src/aggregates/user/mutation';
import {DELETE_QUEUE} from 'src/aggregates/user/mutation';
import {UPDATE_COMPANY_DYNAMICS} from 'src/aggregates/user/mutation';
import {UPDATE_KANBANS} from 'src/aggregates/user/mutation';
import {UPDATE_PASSWORD} from 'src/aggregates/user/mutation';
import {UPDATE_QUEUE} from 'src/aggregates/user/mutation';
import {UPDATE_QUEUE_AGENTS} from 'src/aggregates/user/mutation';
import {UPDATE_USER_CONFIG} from 'src/aggregates/user/mutation';
import {FETCH_COMPANY_CONFIG} from 'src/aggregates/user/queries';
import {FETCH_COMPANY_DYNAMICS} from 'src/aggregates/user/queries';
import {FETCH_KANBANS} from 'src/aggregates/user/queries';
import {FETCH_QUEUES} from 'src/aggregates/user/queries';
import {FETCH_USER_CONFIG} from 'src/aggregates/user/queries';
import {fetch as fetchUser} from 'src/aggregates/user/selectors';
import {authenticate as authenticateUser} from 'src/aggregates/user/xmpp/main';
import {authorize as authorizeUser} from 'src/aggregates/user/xmpp/main';
import {start as startUser} from 'src/aggregates/user/xmpp/main';
import timeout from 'src/aggregates/utils/timeout';
import Metadata from 'src/config/Metadata';
import {logger} from 'src/logger';
import graphis from 'src/services/graphis';


/**
 * TYPES
 */
import {IGraphisError} from 'src/aggregates/index.d';
import {ICreateNPSFeedbackResponse} from 'src/aggregates/user/mutation.d';
import {ICreateQueueResponse} from 'src/aggregates/user/mutation.d';
import {ICreateQueueResult} from 'src/aggregates/user/mutation.d';
import {IDeleteQueueResponse} from 'src/aggregates/user/mutation.d';
import {IDeleteQueueResult} from 'src/aggregates/user/mutation.d';
import {IKanbanInput} from 'src/aggregates/user/mutation.d';
import {IQueueSchedulerInput} from 'src/aggregates/user/mutation.d';
import {IUpdateCompanyDynamicsResponse} from 'src/aggregates/user/mutation.d';
import {IUpdateCompanyDynamicsResult} from 'src/aggregates/user/mutation.d';
import {IUpdateKanbansResponse} from 'src/aggregates/user/mutation.d';
import {IUpdatePasswordResponse} from 'src/aggregates/user/mutation.d';
import {IUpdateQueueAgentsResponse} from 'src/aggregates/user/mutation.d';
import {IUpdateQueueAgentsResult} from 'src/aggregates/user/mutation.d';
import {IUpdateQueueResponse} from 'src/aggregates/user/mutation.d';
import {IUpdateQueueResult} from 'src/aggregates/user/mutation.d';
import {IUpdateUserConfigResponse} from 'src/aggregates/user/mutation.d';
import {ICompanyDynamics} from 'src/aggregates/user/queries.d';
import {IFetchCompanyConfigResponse} from 'src/aggregates/user/queries.d';
import {IFetchCompanyDynamicsResponse} from 'src/aggregates/user/queries.d';
import {IFetchKanbansResponse} from 'src/aggregates/user/queries.d';
import {IFetchQueuesResponse} from 'src/aggregates/user/queries.d';
import {IFetchUserConfigResponse} from 'src/aggregates/user/queries.d';
import {IGraphisCompanyConfig} from 'src/aggregates/user/queries.d';
import {IGraphisUserConfig} from 'src/aggregates/user/queries.d';
import {IKanban} from 'src/aggregates/user/queries.d';
import {IQueue} from 'src/aggregates/user/queries.d';
import {IConfig} from 'src/aggregates/user/state.d';
import {IQuickMessage} from 'src/aggregates/user/state.d';
import {loginErrors} from 'src/aggregates/user/state.d';
import {ICompanyDynamicsInput} from 'src/aggregates/user/utils.d';
import {ILoginTokenResponse} from 'src/aggregates/user/utils.d';
import {IPreAuthenticationData} from 'src/aggregates/user/utils.d';
import {IResponse} from 'src/aggregates/user/utils.d';
import {IAuthorizationData} from 'src/aggregates/user/xmpp/main.d';
import {IAuthorizationResponse} from 'src/aggregates/user/xmpp/main.d';


/**
 * CONSTANTS AND DEFINITIONS
 */
const AUTH_ERRORS: Record<string, loginErrors> = {
    'Error: Authentication Error': loginErrors.AUTHENTICATION,
    'Error: Could not connect to server': loginErrors.UNAVAILABLE,
};

const LOCAL_STORAGE_SERVER_KEY = '@pecazap:server';

const TOKEN_AUTH_TIMEOUT = 10000;


/**
 * CODE
 */

/**
 * I authenticate an user.
 *
 * :param password: user password
 * :param url: server url to connect
 * :param username: username
 *
 * :returns: promise with login error or null
 */
async function authenticate (
    password: string,
    url: string,
    username: string,
): Promise<loginErrors | null>
{
    // try authenticate with base url
    try
    {
        // authenticate user
        await authenticateUser(
            url,
            username,
            password,
        );

        // return response
        return null;
    }

    // authentication failed: try to log error and return error
    catch (error)
    {
        if (logger !== null && logger !== undefined)
        {
            // logger already initialized: log error
            logger.error('CVJPZ0025E', {
                reason: error.stack,
                server: url,
                user: username,
            });
        }

        // return error
        return AUTH_ERRORS[error] ?? loginErrors.UNKNOWN;
    }
}


/**
 * I authorize an user.
 *
 * :param isAdmin: whether the user is an administrator
 * :param url: server url to connect
 * :param username: username
 *
 * :returns: promise with authorization response
 */
async function authorize (
    isAdmin: boolean,
    url: string,
    username: string,
): Promise<IAuthorizationData | null>
{
    // initialize response
    let response: IAuthorizationResponse;

    // request authorization
    try
    {
        response = await authorizeUser(username, isAdmin);
    }

    // authorization failed: log and return null
    catch (error)
    {
        logger.error('CVJPZ0026E', {
            reason: error,
            role: isAdmin ? 'admin' : 'agent',
            user: username,
        });
        return null;
    }

    // error code: return null
    if (response.status >= 300)
    {
        logger.error('CVJPZ0026E', {
            reason: `${response.status}: ${response.params}`,
            role: isAdmin ? 'admin' : 'agent',
            user: username,
        });
        return null;
    }

    // get allowed companies by server
    const companies = Metadata.config.servers[url];

    // get authorization data from response
    const authorization = response?.params?.data;

    // user not allowed to login in server: log and return null
    if (companies?.includes(authorization?.user?.company) !== true)
    {
        logger.error('CVJPZ0032E', {
            company: authorization?.user?.company,
            server: url,
            user: username,
        });
        return null;
    }

    // success code: return authorization response data
    return authorization;
}


/**
 * I create a NPS feedback
 *
 * :param company: user company
 * :param date: feedback date
 * :param name: user name
 * :param response: feedback response
 * :param role: user role
 * :param score: feedback score
 * :param server: user logged server
 * :param user: user id
 *
 * :returns: Promise with nothing
 */
async function createNPSFeedback (
    company: number,
    date: string,
    name: string,
    response: string,
    role: string,
    score: number,
    server: string,
    user: string,
): Promise<void>
{
    // initialize result
    let result: ICreateNPSFeedbackResponse = null;

    // try create nps feedback
    try
    {
        result = await graphis.mutate(CREATE_NPS_FEEDBACK, {
            company,
            date,
            name,
            response,
            role,
            score,
            server,
            user,
        });
    }

    // cannot create nps feedback: log error and return
    catch (error)
    {
        logger.error('CVJPZ0034E', {reason: error, user});
        return;
    }

    // failed to create feedback: log error
    if (result.data.createNPSFeedback === null)
    {
        logger.error('CVJPZ0034E', {
            reason: 'Graphis returns null',
            user,
        });
    }
}


/**
 * I create a queue.
 *
 * :param name: queue name
 * :param scheduler: scheduler data
 *
 * :returns: promise with queue or null
 */
async function createQueue (
    name: string,
    scheduler: IQueueSchedulerInput,
): Promise<IQueue | null>
{
    // initialize response
    let response: ICreateQueueResponse;

    // get user data
    const {company, id: user, name: userName} = fetchUser();

    // try create queue
    try
    {
        response = await graphis.mutate(CREATE_QUEUE, {name, scheduler});
    }

    // failed: log error and return null
    catch (error)
    {
        logger.error(
            'CVJPZ0088E',
            {company, name: userName, queue: name, reason: error.message, user},
        );
        return null;
    }

    // failed: log error and return null
    if (response.data.result.__typename === 'Error')
    {
        const {code, info} = (response.data.result as IGraphisError);
        logger.error(
            'CVJPZ0088E',
            {company, name: userName, queue: name, reason: info ?? code, user},
        );
        return null;
    }

    // return queue data
    return response.data.result as ICreateQueueResult;
}


/**
 * I delete a queue.
 *
 * :param queue: queue id
 *
 * :returns: promise with queue or null
 */
async function deleteQueue (queue: number): Promise<IQueue | null>
{
    // initialize response
    let response: IDeleteQueueResponse;

    // get user data
    const {company, id: user, name: userName} = fetchUser();

    // try delete queue
    try
    {
        response = await graphis.mutate(DELETE_QUEUE, {queue});
    }

    // failed: log error and return null
    catch (error)
    {
        logger.error(
            'CVJPZ0090E',
            {company, name: userName, queue, reason: error.message, user},
        );
        return null;
    }

    // failed: log error and return null
    if (response.data.result.__typename === 'Error')
    {
        const {code, info} = (response.data.result as IGraphisError);
        logger.error(
            'CVJPZ0090E',
            {company, name: userName, queue, reason: info ?? code, user},
        );
        return null;
    }

    // return queue data
    return response.data.result as IDeleteQueueResult;
}


/**
 * I get the first server to fulfill a promise.
 *
 * :param isAdmin: whether user is supervisor or not
 * :param password: user password
 * :param user: username
 *
 * :returns: promise with server name
 */
async function getServer (
    isAdmin: boolean,
    password: string,
    user: string,
): Promise<string | null>
{
    // get last logged server
    let lastServer = localStorage.getItem(LOCAL_STORAGE_SERVER_KEY);

    // last server is defined: try to pre-authenticate it on catraquis
    if (lastServer !== null)
    {
        // pre-authenticate last server on catraquis
        try
        {
            lastServer = await preAuthenticate(
                isAdmin,
                password,
                lastServer,
                user,
            );
        }

        // pre-authentication failed: check error
        catch (error)
        {
            // authorization error: throw error
            if (error instanceof PreAuthorizationError)
            {
                throw error;
            }

            // other error: empty last server
            lastServer = null;
        }
    }

    // last server is not defined: return the first one to respond successfully
    if (lastServer === null)
    {
        // get server from metadata config
        const servers = Object.keys(Metadata.config.servers);

        // set servers promises list
        const promises = servers.map(server => preAuthenticate(
            isAdmin,
            password,
            server,
            user,
        ));

        // save first server to respond successfully or throw error
        await Promise.any(promises)
            .then(server =>
            {
                lastServer = server;
            })
            .catch(({errors}) =>
            {
                for (const error of errors)
                {
                    // authorization error: throw it separately
                    if (error instanceof PreAuthorizationError)
                    {
                        throw error;
                    }
                }

                // throw authentication error
                throw new PreAuthenticationError(errors[errors.length - 1]);
            });
    }

    // return last server
    return lastServer;
}


/**
 * I authenticate an user on catraquis.
 *
 * :param isAdmin: whether user is supervisor or not
 * :param password: user password
 * :param server: server domain
 * :param user: username
 *
 * :returns: promise with login error or null
 */
async function preAuthenticate (
    isAdmin: boolean,
    password: string,
    server: string,
    user: string,
): Promise<string>
{
    // initialize response
    let response: Response;

    // set user role
    const role = isAdmin ? 'supervisor' : 'agent';

    // response data
    let data: IPreAuthenticationData;

    // try to authenticate and authorize
    try
    {
        // request server
        response = await timeout(
            fetch(`https://${server}/sessions`, {
                body: JSON.stringify({password, role, user}),
                method: 'POST',
            }),
            Metadata.config.auth.timeout,
        );

        // load response data
        data = await response.json();
    }

    // request failed: log and throw error
    catch (error)
    {
        logger.error('CVJPZ0043E', {reason: error, server, user});
        throw new PreAuthenticationError(server);
    }

    // unauthorized: log and throw error
    if (response.status === 401 && data?.error === 'authorization')
    {
        logger.error('CVJPZ0043E', {reason: data?.error, server, user});
        throw new PreAuthorizationError(server);
    }

    // request was not successful: log and throw error
    if (response.status !== 201)
    {
        logger.error('CVJPZ0043E', {reason: data?.error, server, user});
        throw new PreAuthenticationError(server);
    }

    // request was successful: return server
    return server;
}


/**
 * I start user.
 *
 * :param id: user id
 * :param rooms: xmpp rooms
 *
 * :returns: promise with nothing
 */
async function start (id: string, rooms: string[]): Promise<void>
{
    // initialize response
    let response: void;

    // request authentication
    try
    {
        response = await startUser(id, rooms);
    }

    // authentication failed: log and return null
    catch (error)
    {
        logger.error('CVJPZ0027E', {reason: error, user: id});
        return null;
    }

    // return response
    return response;
}

/**
 * I authenticate an user on catraquis using its token.
 *
 * :param role: user role
 * :param server: server domain
 * :param token: user session token
 *
 * :returns: promise with response or null
 */
async function authenticateToken (
    role: string,
    server: string,
    token: string,
): Promise<ILoginTokenResponse>
{
    // initialize response
    let response: Response;

    // initialize response content
    let content: IResponse;

    // try to authenticate
    try
    {
        response = await timeout(
            fetch(`https://${server}/sessions/${token}/roles/${role}`, {
                method: 'POST',
            }), TOKEN_AUTH_TIMEOUT,
        );
    }

    // authentication failed: raise LoginTokenError
    catch (error)
    {
        throw new LoginTokenError(error);
    }

    // request failed: raise LoginTokenError
    if (response === null || response === undefined)
    {
        const error = 'Token login failed: empty response';
        throw new LoginTokenError(error);
    }

    // request failed with 401
    if (response.status < 200 || response.status > 299)
    {
        const error = `Token login failed: Error ${response.status}`;
        throw new LoginTokenError(error);
    }

    // extract response fields
    try
    {
        content = await response.json();
    }

    // failed extrecting response fields: raise LoginTokenError
    catch (error)
    {
        throw new LoginTokenError(error);
    }

    // request was successful: return params
    return content.data;
}


/**
 * I get company config
 *
 * :returns: company config
 */
async function getCompanyConfig(): Promise<IGraphisCompanyConfig | null>
{
    // initialize response
    let response: IFetchCompanyConfigResponse;

    // get id, name and company from user
    const {id, name, company} = fetchUser();

    // fetch configurations
    try
    {
        response = await graphis.query(FETCH_COMPANY_CONFIG);
    }

    // load company configurations failed: log error and return null
    catch (error)
    {
        logger.error('CVJPZ0063E', {
            company,
            name: name,
            reason: error,
            user: id,
        });
        return null;
    }

    // company config failed with other reason: log error and return null
    if (response.data.companyConfig.__typename === 'Error')
    {
        // get company config error
        const error = response.data.companyConfig as IGraphisError;

        // log error
        logger.error('CVJPZ0063E', {
            company,
            name: name,
            reason: error.info ?? error.code,
            user: id,
        });
        return null;
    }

    // return company config
    return response.data.companyConfig as IGraphisCompanyConfig;
}


/**
 * I get company dynamics.
 *
 * :returns: promise with company dynamics or null
 */
async function getCompanyDynamics (): Promise<ICompanyDynamics | null>
{
    // initialize response
    let response: IFetchCompanyDynamicsResponse;

    // get user data
    const {company, id: user, name} = fetchUser();

    // fetch company dynamics
    try
    {
        response = await graphis.query(FETCH_COMPANY_DYNAMICS);
    }

    // failed: log error and return null
    catch (error)
    {
        logger.error(
            'CVJPZ0085E',
            {company, name, reason: error.message, user},
        );
        return null;
    }

    // failed: log error and return null
    if (response.data.companyDynamics.__typename === 'Error')
    {
        const {code, info} = (response.data.companyDynamics as IGraphisError);
        logger.error(
            'CVJPZ0085E',
            {company, name, reason: info ?? code, user},
        );
        return null;
    }

    // return company dynamics
    return response.data.companyDynamics as ICompanyDynamics;
}


/**
 * I get kanban list.
 *
 * :returns: kanban list or null
 */
async function getKanbans(): Promise<IKanban[] | null>
{
    // initialize response
    let response: IFetchKanbansResponse;

    // fetch kanban
    try
    {
        response = await graphis.query(FETCH_KANBANS);
    }

    // failed: log error and return null
    catch (error)
    {
        // get id, name and company from user
        const {id, name, company} = fetchUser();

        // log error
        logger.error('CVJPZ0092E', {
            company,
            name: name,
            reason: error.info ?? error.code,
            user: id,
        });
        return null;
    }

    // return kanban list
    return response.data.kanbans;
}


/**
 * I get queues.
 *
 * :returns: promise with queues or null
 */
async function getQueues (): Promise<IQueue[] | null>
{
    // initialize response
    let response: IFetchQueuesResponse;

    // get user data
    const {company, id: user, name} = fetchUser();

    // fetch queues
    try
    {
        response = await graphis.query(FETCH_QUEUES);
    }

    // failed: log error and return null
    catch (error)
    {
        logger.error(
            'CVJPZ0087E',
            {company, name, reason: error.message, user},
        );
        return null;
    }

    // return queues
    return response.data.queues;
}


/**
 * I get user config
 *
 * :returns: user config
 */
async function getUserConfig(): Promise<IConfig | null>
{
    // initialize response
    let response: IFetchUserConfigResponse;

    // fetch user config
    try
    {
        response = await graphis.query(FETCH_USER_CONFIG);
    }

    // load user configurations failed: log error and return null
    catch (error)
    {
        // get id, name and company from user
        const {id, name, company} = fetchUser();

        // log error
        logger.error('CVJPZ0065E', {
            company,
            name: name,
            reason: error.info ?? error.code,
            user: id,
        });
        return null;
    }

    // get response data
    const data = response.data.userConfig as IGraphisUserConfig;

    // reduce messages
    const messages = data?.messages?.map(({body, key}) => ({body, key})) ?? [];

    // return user config
    return {
        ai: {
            apiKey: data?.ai?.apiKey ?? '',
        },
        answerAlert: data?.answerAlert ?? 0,
        menu: data?.menu ?? 'regular',
        messages,
        prompts: {
            qualityReport: data?.prompts?.qualityReport ?? '',
        },
    };
}


/**
 * I reduce set up quick messages
 *
 * :param shortcuts: list of quick messages
 * :param admin: wheter user is admin
 *
 * :returns: list of messages with proper format
 */
function reduceQuickMessages (
    shortcuts: IQuickMessage[],
    admin: boolean,
): IQuickMessage[]
{
    // user is admin: return unreduced quick messages
    if (admin === true)
    {
        return shortcuts;
    }

    // initiate reduce quick messages list
    const reducedShortcuts: IQuickMessage[] = [];

    // add forward slash to each shortcut key
    shortcuts.forEach((shortcut) => reducedShortcuts.push(
        {
            body: shortcut.body,
            key: `/${shortcut.key}`,
        },
    ));

    // return reduced quick messages
    return reducedShortcuts;
}


/**
 * I update company dynamics.
 *
 * :param input: company dynamics to update
 *
 * :returns: promise with boolean (true = success | false = failed)
 */
async function updateCompanyDynamics (
    input: ICompanyDynamicsInput[],
): Promise<boolean>
{
    // initialize response
    let response: IUpdateCompanyDynamicsResponse;

    // get user data
    const {company, id: user, name} = fetchUser();

    // update company dynamics
    try
    {
        response = await graphis.mutate(
            UPDATE_COMPANY_DYNAMICS,
            {input},
        );
    }

    // failed: log error and return null
    catch (error)
    {
        logger.error(
            'CVJPZ0086E',
            {company, name, reason: error.message, user},
        );
        return null;
    }

    // failed: log error and return null
    if (response.data.result.__typename === 'Error')
    {
        const {code, info} = (response.data.result as IGraphisError);
        logger.error(
            'CVJPZ0086E',
            {company, name, reason: info ?? code, user},
        );
        return null;
    }

    // return result
    return (response.data.result as IUpdateCompanyDynamicsResult).success;
}


/**
 * I update kanbans.
 *
 * :param input: updated kanbans list
 *
 * :returns: updated kanbans list or null
 */
async function updateKanbans(input: IKanbanInput[]): Promise<IKanban[] | null>
{
    // initialize response
    let response: IUpdateKanbansResponse;

    // update kanbans
    try
    {
        response = await graphis.mutate(UPDATE_KANBANS, {columns: input});
    }

    // failed: log error and return null
    catch (error)
    {
        // get id, name and company from user
        const {id, name, company} = fetchUser();

        // log error
        logger.error('CVJPZ0093E', {
            company,
            name: name,
            reason: error.info ?? error.code,
            user: id,
        });
        return null;
    }

    // return updated kanbans list
    return response.data.result;
}


/**
 * I update user password.
 *
 * :param old: old password
 * :param _new: new password
 *
 * :returns: true (success) of null (failure)
 */
async function updatePassword (
    old: string,
    _new: string,
): Promise<boolean | null>
{
    // initialize response
    let response: IUpdatePasswordResponse;

    // get user data
    const {id: user, company} = fetchUser();

    // try update password
    try
    {
        response = await graphis.mutate(
            UPDATE_PASSWORD,
            {_new, old},
        );
    }

    // cannot update password: log error and return null
    catch (error)
    {
        logger.error('CVJPZ0070E', {
            company,
            reason: error,
            user,
        });
        return null;
    }

    // failed: log error and return null
    if (response.data.updatePassword.__typename === 'Error')
    {
        const {code, info} = (response.data.updatePassword as IGraphisError);
        logger.error('CVJPZ0070E', {
            company,
            reason: info ?? code,
            user,
        });
        return null;
    }

    // return success
    return true;
}


/**
 * I update a queue.
 *
 * :param queue: queue id
 * :param name: queue name
 * :param scheduler: scheduler data
 *
 * :returns: promise with queue or null
 */
async function updateQueue (
    queue: number,
    name: string,
    scheduler: IQueueSchedulerInput,
): Promise<IQueue | null>
{
    // initialize response
    let response: IUpdateQueueResponse;

    // get user data
    const {company, id: user, name: userName} = fetchUser();

    // try update queue
    try
    {
        response = await graphis.mutate(UPDATE_QUEUE, {name, queue, scheduler});
    }

    // failed: log error and return null
    catch (error)
    {
        logger.error(
            'CVJPZ0089E',
            {company, name: userName, queue, reason: error.message, user},
        );
        return null;
    }

    // failed: log error and return null
    if (response.data.result.__typename === 'Error')
    {
        const {code, info} = (response.data.result as IGraphisError);
        logger.error(
            'CVJPZ0089E',
            {company, name: userName, queue, reason: info ?? code, user},
        );
        return null;
    }

    // return queue data
    return response.data.result as IUpdateQueueResult;
}


/**
 * I update queue agents.
 *
 * :param queue: queue id
 * :param agents: queue agents
 *
 * :returns: promise with queue or null
 */
async function updateQueueAgents (
    queue: number,
    agents: string[],
): Promise<IQueue | null>
{
    // initialize response
    let response: IUpdateQueueAgentsResponse;

    // get user data
    const {company, id: user, name: userName} = fetchUser();

    // try update queue agents
    try
    {
        response = await graphis.mutate(UPDATE_QUEUE_AGENTS, {agents, queue});
    }

    // failed: log error and return null
    catch (error)
    {
        logger.error(
            'CVJPZ0091E',
            {company, name: userName, queue, reason: error.message, user},
        );
        return null;
    }

    // failed: log error and return null
    if (response.data.result.__typename === 'Error')
    {
        const {code, info} = (response.data.result as IGraphisError);
        logger.error(
            'CVJPZ0091E',
            {company, name: userName, queue, reason: info ?? code, user},
        );
        return null;
    }

    // return queue data
    return response.data.result as IUpdateQueueAgentsResult;
}


/**
 * I update user config
 *
 * :param config: user config
 *
 * :returns: user config
 */
async function updateUserConfig (
    config: Partial<IConfig>,
): Promise<IConfig | null>
{
    // initialize response
    let response: IUpdateUserConfigResponse;

    // try update user config
    try
    {
        response = await graphis.mutate(UPDATE_USER_CONFIG, {input: config});
    }

    // cannot update user config: log error and return null
    catch (error)
    {
        // get id, name and company from user
        const {id, name, company} = fetchUser();

        // log error
        logger.error('CVJPZ0066E', {
            company,
            name: name,
            reason: error.info ?? error.code,
            user: id,
        });
        return null;
    }

    // get response data
    const data = response.data.updateUserConfig as IGraphisUserConfig;

    // typename is error: log error and return null
    if (data.__typename === 'Error')
    {
        // get response as error
        const error = response.data.updateUserConfig as IGraphisError;

        // get id, name and company from user
        const {id, name, company} = fetchUser();

        // log error
        logger.error('CVJPZ0066E', {
            company,
            name: name,
            reason: error.info ?? error.code,
            user: id,
        });
        return null;
    }

    // reduce messages
    const messages = data?.messages?.map(({body, key}) => ({body, key})) ?? [];

    // return updated user configuration
    return {
        ai: {
            apiKey: data?.ai?.apiKey ?? '',
        },
        answerAlert: data?.answerAlert ?? 0,
        menu: data?.menu ?? 'regular',
        messages,
        prompts: {
            qualityReport: data?.prompts?.qualityReport ?? '',
        },
    };
}


/**
 * EXPORTS
 */
export {
    authenticate,
    authenticateToken,
    authorize,
    createNPSFeedback,
    createQueue,
    deleteQueue,
    getCompanyConfig,
    getCompanyDynamics,
    getKanbans,
    getQueues,
    getServer,
    getUserConfig,
    preAuthenticate,
    reduceQuickMessages,
    start,
    updateCompanyDynamics,
    updateKanbans,
    updatePassword,
    updateQueue,
    updateQueueAgents,
    updateUserConfig,
};
